import React, { useState, useEffect } from "react"
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material"
import { CashMovementPayload } from "../models/pos"
import { useTranslation } from "react-i18next"

const REASON_CODES = [
    { code: "bank_deposit" },
    { code: "change_fund" },
    { code: "expense" },
    { code: "correction" },
    { code: "other" },
]

type CashMovementFormProps = {
    open: boolean
    onClose: () => void
    onSubmit: (payload: CashMovementPayload) => void | Promise<void>
    loading: boolean
    outletId?: string
    registerId?: string
    cashSessionId?: string
}

function CashMovementForm({
    open,
    onClose,
    onSubmit,
    loading,
    outletId,
    registerId,
    cashSessionId,
}: CashMovementFormProps) {
    const { t } = useTranslation()
    const [direction, setDirection] = useState<"in" | "out">("out")
    const [amount, setAmount] = useState("")
    const [reasonCode, setReasonCode] = useState("")
    const [note, setNote] = useState("")

    useEffect(() => {
        if (open) {
            setDirection("out")
            setAmount("")
            setReasonCode("")
            setNote("")
        }
    }, [open])

    function handleSubmit() {
        const amountMinor = Math.round(parseFloat(amount || "0") * 100)

        onSubmit({
            outletId,
            registerId,
            cashSessionId,
            direction,
            amountMinor,
            currency: "EUR",
            reasonCode,
            note: note || undefined,
        })
    }

    const isValid = amount && parseFloat(amount) > 0 && reasonCode

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    m: { xs: 2, sm: 3 },
                },
            }}
        >
            <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                {t("cashMovementForm.title")}
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                    <InputLabel id="direction-label">
                        {t("cashMovementForm.direction")}
                    </InputLabel>
                    <Select
                        labelId="direction-label"
                        label={String(t("cashMovementForm.direction"))}
                        value={direction}
                        onChange={(e) =>
                            setDirection(e.target.value as "in" | "out")
                        }
                        disabled={loading}
                        sx={{ minHeight: 48 }}
                    >
                        <MenuItem value="in" sx={{ minHeight: 44 }}>
                            {t("cashMovementForm.directionIn")}
                        </MenuItem>
                        <MenuItem value="out" sx={{ minHeight: 44 }}>
                            {t("cashMovementForm.directionOut")}
                        </MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label={String(t("cashMovementForm.amount"))}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={loading}
                    inputMode="decimal"
                    sx={{
                        mb: 2,
                        "& .MuiInputBase-root": {
                            minHeight: 48,
                            fontSize: { xs: "1rem", sm: "1rem" },
                        },
                    }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="reason-label">
                        {t("cashMovementForm.reason")}
                    </InputLabel>
                    <Select
                        labelId="reason-label"
                        label={String(t("cashMovementForm.reason"))}
                        value={reasonCode}
                        onChange={(e) => setReasonCode(e.target.value)}
                        disabled={loading}
                        sx={{ minHeight: 48 }}
                    >
                        {REASON_CODES.map((r) => (
                            <MenuItem
                                key={r.code}
                                value={r.code}
                                sx={{ minHeight: 44 }}
                            >
                                {t(`cashMovementForm.reasons.${r.code}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label={String(t("cashMovementForm.note"))}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={loading}
                    multiline
                    rows={2}
                    sx={{
                        "& .MuiInputBase-root": {
                            fontSize: { xs: "1rem", sm: "1rem" },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    sx={{
                        minHeight: 48,
                        px: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                >
                    {t("common.cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isValid || loading}
                    sx={{
                        minHeight: 48,
                        px: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                >
                    {t("cashMovementForm.submit")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CashMovementForm
